import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Axios from "../config/axios";
import { UrlConstant } from "../commom/UrlConstant";

export interface CurrencyState {
    code: string;
    symbol: string;
    isSupported: boolean;
    effectiveCurrency: string;
    loading: boolean;
    error: string | null;
}

const initialState: CurrencyState = {
    code: "",
    symbol: "",
    isSupported: true,
    effectiveCurrency: "",
    loading: false,
    error: null,
};

interface CurrencyResponse {
    code: string;
    symbol: string;
    isSupported: boolean;
    effectiveCurrency: string;
}

// Async thunk to fetch user currency
export const fetchUserCurrency = createAsyncThunk<
    CurrencyResponse,
    void,
    { rejectValue: string }
>(
    "currency/fetchUserCurrency",
    async (_, { rejectWithValue }) => {
        try {
            const response = await Axios.get(UrlConstant.User.GetUserCurrency);

            if (response.data.success) {
                return response.data.currency as CurrencyResponse;
            }

            return rejectWithValue("Failed to fetch currency");
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch currency");
        }
    }
);

const currencySlice = createSlice({
    name: "currency",
    initialState,
    reducers: {
        resetCurrencyState(state) {
            state.code = "USD";
            state.symbol = "$";
            state.isSupported = true;
            state.effectiveCurrency = "USD";
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserCurrency.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserCurrency.fulfilled, (state, action: PayloadAction<CurrencyResponse>) => {
                state.code = action.payload.code || "USD";
                state.symbol = action.payload.symbol || "$";
                state.isSupported = action.payload.isSupported ?? true;
                state.effectiveCurrency = action.payload.effectiveCurrency || "USD";
                state.loading = false;
                state.error = null;
            })
            .addCase(fetchUserCurrency.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) || "Failed to fetch currency";
            });
    },
});

export const { resetCurrencyState } = currencySlice.actions;
export default currencySlice.reducer;
